import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Ticket, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AddToCalendar from "@/components/AddToCalendar";

// Concert ticket purchase success page. Polls the concerts/checkout/status
// endpoint until Stripe reports "paid", then displays the newly-issued
// ticket(s) and QR tokens.

export default function ConcertPurchaseSuccess() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: null, tickets: [], concert_id: null });
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) { setError("Missing session id"); return; }
    let cancelled = false;

    const poll = async () => {
      for (let i = 0; i < 10; i++) {
        if (cancelled) return;
        try {
          const r = await api.get(`/concerts/checkout/status/${sessionId}`);
          setStatus(r.data);
          setAttempts(i + 1);
          if (r.data.payment_status === "paid") return;
          if (["expired", "canceled"].includes(r.data.status)) return;
        } catch (e) {
          setError(formatApiError(e.response?.data?.detail) || e.message);
          return;
        }
        await new Promise((res) => setTimeout(res, 2000));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = status.payment_status === "paid";
  const failed = ["expired", "canceled"].includes(status.status);

  return (
    <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="concert-purchase-success">
      {paid ? (
        <>
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle2 size={30} className="text-emerald-700" />
          </div>
          <h1 className="mt-5 text-3xl font-black tracking-tight text-[#1B1614]">You're going!</h1>
          <p className="mt-3 text-[15px] text-[#5C4E48]">
            {status.tickets.length} ticket{status.tickets.length !== 1 ? "s" : ""} issued. We&rsquo;ve saved them to your account — show your QR at the door.
          </p>
          <Card className="mt-6 rounded-md p-5 text-left">
            <p className="text-xs uppercase tracking-wider font-mono text-[#5C4E48] mb-2">Your tickets</p>
            {status.tickets.map((tk, i) => (
              <div key={tk.id} className="border-t border-[#EAE3DE] first:border-t-0 py-2" data-testid={`success-ticket-${tk.id}`}>
                <p className="font-semibold text-sm">Ticket #{i + 1}{tk.tier_name ? ` · ${tk.tier_name}` : ""}</p>
                <p className="font-mono text-[10px] text-[#8B7B73] break-all">{tk.qr_token}</p>
              </div>
            ))}
          </Card>
          <div className="mt-6 flex flex-col gap-2">
            {status.concert && (
              <AddToCalendar
                testId="success-add-to-calendar"
                event={{
                  title: `${status.concert.title}${status.concert.artist_stage_name ? ` — ${status.concert.artist_stage_name}` : ""}`,
                  description: status.concert.description,
                  location: [status.concert.venue_name, status.concert.address, status.concert.city, status.concert.state].filter(Boolean).join(", "),
                  starts_at: status.concert.starts_at,
                  ends_at: status.concert.ends_at,
                  uid: status.concert.id,
                  url: `${window.location.origin}/concerts/${status.concert.id}`,
                }}
              />
            )}
            <Button onClick={() => navigate("/my/tickets")} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="success-view-tickets">
              <Ticket size={14} className="mr-1.5" /> View my tickets
            </Button>
            {status.concert_id && (
              <Link to={`/concerts/${status.concert_id}`} className="text-sm text-[#B31E1E] hover:underline">
                Back to event
              </Link>
            )}
          </div>
        </>
      ) : failed || error ? (
        <>
          <h1 className="text-3xl font-black tracking-tight text-[#B31E1E]">Checkout didn&rsquo;t complete</h1>
          <p className="mt-3 text-[15px] text-[#5C4E48]">
            {error || "Your card wasn't charged. You can try again from the event page."}
          </p>
          <Button onClick={() => navigate("/events")} className="mt-6 rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]">Back to events</Button>
        </>
      ) : (
        <>
          <Loader2 size={30} className="mx-auto text-[#B31E1E] animate-spin" />
          <p className="mt-4 text-[#5C4E48]">Finalizing your tickets{attempts > 0 ? ` (${attempts}/10)` : ""}…</p>
        </>
      )}
    </div>
  );
}
